<template>
  <div class="search-component">
    <b-row
      v-if="showDesktopInput"
      class="desktop-screen"
    >
      <b-col
        sm="10"
        md="10"
        lg="auto"
        xl="auto"
        class="search-width"
      >
        <b-input-group
          class="vt-search-bar float-component"
          :class="{'vt-search-bar-done' : searched}"
          size="sm"
        >
          <b-input-group-prepend>
            <b-input-group-text>
              <fa-icon :icon="['fal', 'barcode-alt']" />
            </b-input-group-text>
          </b-input-group-prepend>
          <b-form-input
            :id="uniqueId"
            ref="searchBarInput"
            v-model="search"
            v-focus
            qa="searcher-module-barcode-input"
            :name="name"
            :placeholder="`${ $t('global.enterIdentifier') }...`"
            type="text"
            @focus.native="$event.target.select()"
            @input="clearState"
            @keyup.enter="searchEvent"
          />
          <b-input-group-append>
            <div class="search-button-wrapper">
              <b-button
                v-if="searched"
                qa="searcher-module-clear-button"
                class="btn__icon btn__icon--primary  alt-btn px-1 py-0"
                @click="clearSearch"
              >
                <i
                  class="far fa-times"
                  aria-hidden="true"
                />
              </b-button>
              <b-button
                qa="searcher-module-search-button"
                class="btn__icon btn__icon--primary px-2 py-0"
                @click="searchEvent"
              >
                <i
                  class="fas fa-search"
                  aria-hidden="true"
                />
              </b-button>
            </div>
            <div
              v-if="!searched"
              class="search-button-wrapper"
            >
              <b-btn
                v-show="entitySelected && entitySelected.scope !== 'patient'"
                :id="popoverId"
                class="options-button"
                size="sm"
              >
                <i
                  class="fas fa-ellipsis-v"
                  aria-hidden="true"
                />
              </b-btn>
              <b-popover
                :target="popoverId"
                triggers="click blur"
                placement="bottomleft"
              >
                <div class="radio-box">
                  <b-form-group>
                    <b-form-radio-group
                      v-model="selected"
                      class="radio-btns"
                      name="radio-options"
                      :options="filteredOptions"
                    />
                  </b-form-group>
                </div>
              </b-popover>
            </div>
          </b-input-group-append>
        </b-input-group>
      </b-col>
    </b-row>

    <!--Touch screen-->
    <b-row
      v-else-if="showTouchInput"
      class="touch-screen float-component"
    >
      <b-col
        class="touch-screen px-0"
        cols="12"
      >
        <b-input-group
          class="vt-search-bar"
          :class="{'vt-search-bar-done' : searched}"
          size="sm"
        >
          <b-input-group-prepend>
            <b-input-group-text>
              <fa-icon :icon="['fal', 'barcode-alt']" />
            </b-input-group-text>
          </b-input-group-prepend>

          <b-form-input
            :id="uniqueId"
            ref="searchBarInput"
            v-model="search"
            v-focus
            qa="searcher-module-touch-mode-input"
            :name="name"
            :placeholder="`${ $t('global.enterIdentifier') }...`"
            type="text"
            @focus.native="$event.target.select()"
            @input="clearState"
            @keyup.enter="searchEvent"
          />

          <b-input-group-append>
            <div class="search-button-wrapper">
              <b-btn
                v-if="!searched"
                qa="searcher-module-search-button"
                class="search-button"
                size="sm"
                @click="searchEvent"
              >
                <i
                  class="fas fa-search"
                  aria-hidden="true"
                />
              </b-btn>
              <b-btn
                v-if="searched"
                qa="searcher-module-clear-button"
                class="search-button"
                size="sm"
                @click="clearSearch"
              >
                <i
                  class="far fa-times"
                  aria-hidden="true"
                />
              </b-btn>
            </div>
          </b-input-group-append>
        </b-input-group>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { IDENTIFIER_SCOPES } from '@/api/resources/identifierScopesResource'
import { uniqueId } from 'lodash'

export default {
    name: 'SearcherModule',
    props: {
        popoverId: { type: String, default: 'popover-options' },
        initMode: { type: Object },
        isGeneralDisabled: { type: Boolean, default: false },
        isCurrentDisabled: { type: Boolean, default: false },
        defaultSearchType: { type: String, default: 'general' },
        patientSearch: { type: Boolean, default: false },
        requestSearch: { type: Boolean, default: false },
        subrequestSearch: { type: Boolean, default: false },
        caseSearch: { type: Boolean, default: false },
        sampleSearch: { type: Boolean, default: false },
        portionSearch: { type: Boolean, default: false },
        subportionSearch: { type: Boolean, default: false },
        errorMessage: { type: String, required: true },
        name: { type: String, required: true },
        clearInputAfterSearch: { type: Boolean, default: false },
        restoreSearchState: { type: Boolean, default: true }
    },
    data() {
        return {
            contentTrackingRouteName: 'ContentTracking',
            search: '',
            searched: false,
            selected: this.defaultSearchType,
            entitySelected: null,
            options: [
                { value: 'general', text: this.$t('component.searcher.general') },
                { value: 'currentPage', text: this.$t('component.searcher.currentPage') }
            ],
            optionsIcons: [
                { value: 'patient', icon: 'fal fa-user-circle', text: this.$t('component.searcher.patient') },
                { value: 'other', icon: 'fas fa-th-large', text: this.$t('component.searcher.allEntities') }
            ]
        }
    },
    computed: {
        filteredOptions() {
            if (this.isGeneralDisabled) {
                return this.options.filter((option) => option.value === 'currentPage')
            }
            if (this.isCurrentDisabled) {
                return this.options.filter((option) => option.value === 'general')
            }
            return this.options
        },
        regex() {
            return this.$store.getters.regex
        },
        uniqueId() {
            let prefix = null
            if (this.$route.name === this.contentTrackingRouteName) {
                prefix = 'searchBarInputTouch'
            } else {
                prefix = 'searchBarInputDesktop'
            }
            return uniqueId(prefix)
        },
        showDesktopInput() {
            return this.$route.name !== this.contentTrackingRouteName
        },
        showTouchInput() {
            return this.$route.name === this.contentTrackingRouteName
        }
    },
    mounted() {
        this.mode = this.$store.getters.searcherEntityState || this.initMode
        this.selected = this.defaultSearchType
        if (this.restoreSearchState) {
            const searchState = this.$store.getters.searchState
            if (searchState) {
                Object.assign(this.$data, searchState)
            }
        }
        this.$eventHub.$on('focus-searcherModule', this.focusInput)
    },
    beforeDestroy() {
        this.$eventHub.$off('focus-searcherModule', this.focusInput)
    },
    methods: {
        onEntitySelected(option) {
            this.$store.commit('updateSearcherEntityState', option)
        },
        saveSearchState() {
            this.$store.commit('updateSearchState', this.$data)
        },
        searchEvent() {
            this.searched = true
            this.$emit('submitSearch', this.search)
            const searchBarInput = document.getElementById(this.uniqueId)
            searchBarInput.select()
            const searchInformation = { search: this.search.trim(), searchType: this.selected }
            if (this.clearInputAfterSearch) {
                this.clearSearch()
            }
            for (let regex of this.regex) {
                let regexObj = new RegExp('^(?:' + regex.regex + ')$')
                if (regexObj.test(searchInformation.search)) {
                    if (regex.identifierType.idtScope === IDENTIFIER_SCOPES.CASE && this.caseSearch) {
                        this.$emit('globalSearch', searchInformation)
                        this.$emit('studySearch', searchInformation)
                        this.saveSearchState()
                        return
                    } else if (regex.identifierType.idtScope === IDENTIFIER_SCOPES.PERSON && this.patientSearch) {
                        searchInformation.identifierType = regex.identifierType.code
                        this.$emit('globalSearch', searchInformation)
                        this.$emit('patientNIFSearch', searchInformation)
                        this.saveSearchState()
                        return
                    } else if (regex.identifierType.idtScope === IDENTIFIER_SCOPES.CENTER_PATIENT && this.patientSearch) {
                        searchInformation.identifierType = regex.identifierType.code
                        this.$emit('globalSearch', searchInformation)
                        this.$emit('patientNHCSearch', searchInformation)
                        this.saveSearchState()
                        return
                    } else if (regex.identifierType.idtScope === IDENTIFIER_SCOPES.REQUEST && this.requestSearch) {
                        this.$emit('globalSearch', searchInformation)
                        this.$emit('requestSearch', searchInformation)
                        this.saveSearchState()
                        return
                    } else if (regex.identifierType.idtScope === IDENTIFIER_SCOPES.SUBREQUEST && this.subrequestSearch) {
                        this.$emit('globalSearch', searchInformation)
                        this.$emit('subrequestSearch', searchInformation)
                        this.saveSearchState()
                        return
                    } else if (regex.identifierType.idtScope === IDENTIFIER_SCOPES.SAMPLE && this.sampleSearch) {
                        this.$emit('globalSearch', searchInformation)
                        this.$emit('sampleSearch', searchInformation)
                        this.saveSearchState()
                        return
                    } else if (regex.identifierType.idtScope === IDENTIFIER_SCOPES.PORTION && this.portionSearch) {
                        this.$emit('globalSearch', searchInformation)
                        this.$emit('portionSearch', searchInformation)
                        this.saveSearchState()
                        return
                    } else if (regex.identifierType.idtScope === IDENTIFIER_SCOPES.SUBPORTION && this.subportionSearch) {
                        this.$emit('globalSearch', searchInformation)
                        this.$emit('subportionSearch', searchInformation)
                        this.saveSearchState()
                        return
                    } else if (regex.identifierType.idtScope === IDENTIFIER_SCOPES.INCIDENT) {
                        this.$emit('incidentSearch', searchInformation)
                        this.saveSearchState()
                        return
                    } else if (regex.identifierType.idtScope === IDENTIFIER_SCOPES.SHIPMENT) {
                        this.$emit('shipmentSearch', searchInformation)
                        this.saveSearchState()
                        return
                    }
                }
            }
            this.showErrorModalGlobal(this.errorMessage)
        },
        clearSearch() {
            this.searched = false
            this.search = ''
            this.focusInput()
            this.cleanSearchStateGlobalMixin()
            this.$emit('clearSearch')
        },
        clearState() {
            this.searched = false
        },
        focusInput() {
            this.$nextTick(() => document.getElementById(this.uniqueId)?.focus())
        }
    }
}

</script>

<style lang="scss">

/*--Dejamos esta línea comentada por si fuera necesario borrar el círculo central de los radio buttons--*/
/* .custom-radio .custom-control-input:checked ~ .custom-control-label::after{
  background-image: none;
} */

.radio-btns{
  display: flex;
  flex-direction: column;
}

.radio-box .form-group{
  margin-bottom: 0 !important;
}

.radio-btns .custom-radio{
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
  margin: 0;
  padding: 5px 30px;
}

.vt-search-bar-done ::placeholder{
  color: #FFF !important;
  opacity: 1 !important;
}

.search-component .search-combo{
  max-width: 120px;
}

.search-width{
  max-width: 280px !important;
}

.search-component .search-combo .multiselect__tags{
  border-radius: 5px !important;
  border: 0 !important;
  box-shadow: 0 1px 5px 0 #9c9c9c;
  height: 28px;
}

.search-component .entity-selector .multiselect__tags{
  height: 28px;
}

.search-component .custom-select{
  background: none;
  background-color: white;
  border: 0;
  box-shadow: 0 1px 5px 0 #9c9c9c;
  border-radius: 5px;
}

.search-component .multiselect__content-wrapper{
  min-width: 100%;
  width: auto !important;
  overflow: hidden;
  overflow-y: auto;
}

.search-component .multiselect--small .multiselect__tags{
  border-radius: 5px;
  box-shadow: 0 1px 5px 0 vt-color(search-bar-box-shadow);
  background: vt-color(search-bar-bg);
  display: flex;
  align-items: center;
  padding-top: 0;
  padding-bottom: 0;
}

.search-component .multiselect--small .multiselect__select{
  align-items: center;
  width: 28px;
}

.search-component .multiselect--small .multiselect__select::before{
  top: 12px;
}

.search-component .multiselect--small .multiselect__single{
  width: 20px;
  padding: 0;
  display: flex;
  align-items: center;
  margin-top: 0;
}

.search-component .multiselect--small .multiselect__single i{
  font-size: 18px;
}

.search-component .multiselect--small .multiselect__element span{
  padding: 12px;
}

.search-component .multiselect--small .multiselect__element i{
  font-size: 18px;
}

</style>

<style scoped lang="scss">

.search-component .fa-th-large{
  font-size: 13px;
}

.search-component .touch-screen{
  display: none;
}

.vt-search-bar {
  height: 30px;
  .input-group-prepend .input-group-text {
    border-radius: 14px;
    color: vt-color(search-bar-text);
    border: none;
    background-color: inherit;
    font-size:13px;
    padding:0.7rem;
  }
  input {
    border: none;
    color: vt-color(search-bar-text);
    background-color: inherit;
    padding-top:0.4rem;
  }
  .icon-search {
    color: white;
    font-size:0.8rem;
  }
}

.vt-search-bar-done{
  background-color: vt-color(white-light);
  color: vt-color(primary);
}

.vt-search-bar-done .form-control{
  color: vt-color(primary);
}

.vt-search-bar-done .search-button i{
  color: #FFF;
}

.vt-search-bar-done .search-button:hover{
  background-color: vt-color(dark-primary, .2);
  color: #FFF;
}

.search-component .btn-secondary:not(:disabled):not(.disabled):active, .btn-secondary:not(:disabled):not(.disabled).active, .show > .btn-secondary.dropdown-toggle{
  background-color: #FFF;
}

.search-button-wrapper {
  display: flex;
  align-items: center;
}

.search-button {
  border-radius: 0 5px 5px 0;
  width: 30px;
  line-height: 1;
  color: inherit;
  background-color: vt-color(search-bar-bg);
  border:transparent;
  height:95%;
}

.search-button i{
  color: vt-color(primary);
  font-size: 14px;
}

.search-button:hover{
  background-color: #FFF;
}

.options-button {
  border-radius: 0 5px 5px 0;
  width: 25px;
  line-height: 1;
  color: inherit;
  background-color: vt-color(search-bar-bg);
  border:transparent;
  height:95%;

}

.options-button i{
  color: vt-color(primary);
  font-size: 14px;
}

.options-button:hover{
  background-color: #FFF;
}

.icon-search {
    color: white;
    font-size:1.1rem;
}

.input-group-append{
  z-index: 3;
}

.vt-search-bar-done .search-button{
    width: 40px !important;
    background-color: vt-color(dark-primary);
}

@media (max-width: 1370px){
    .vt-search-bar {
  .input-group-prepend .input-group-text {
    font-size:13px;
    padding:0.6rem;
  }
  input {
    font-size:0.85rem;
    padding-top:0.4rem;
  }
  .icon-search {
    color: white;
    font-size:0.8rem;
}
}

@media (max-width: 1024px){
  .search-component .search-combo{
    max-width: 100%;
}
}

}

</style>
